/**
 * @fileoverview Default values for complex objects (DTOs, interfaces, and data structures).
 *
 * This file contains frozen default objects that serve as:
 * - Placeholders in UI components before real data loads (Mostly in vue list items)
 * - Fallback values when API calls fail or return incomplete data
 * - Initial state for forms and data structures
 * - Mock data for testing and development
 *
 * NOTE: Please don't import this file directly, but use the index file instead.
 */
import { MiaPlaza } from "@/Reinforced.Typings";
import { DefaultText, DefaultJSON, DefaultUrl } from "./primitives";
import { DefaultAvatarPictureTransparent, DefaultImageUrl, DefaultPicture } from "./images";
import { DefaultDateString, DefaultDuration, DefaultInstantString, DefaultLocalDateTime, DefaultTime } from "./temporal";
import { DefaultSubject } from "./enums";
import { defaultModelId } from "./custom";

export const DefaultReceiptData = Object.freeze<MiaPlaza.Controllers.Payments.IReceiptData>({
	ParentName: DefaultText,
	ParentEmail: DefaultText,
	PaymentDate: DefaultDateString,
	StudentName: DefaultText,
	Amount: DefaultText,
	Product: MiaPlaza.Model.Payments.ProductType.OneMonth,
	PaymentMethodData: {
		Description: DefaultText,
		PaymentMethodType: DefaultText,
	},
});

export const DefaultAuthorData = Object.freeze<MiaPlaza.Controllers.Shop.IAuthorData>({
	Id: defaultModelId(),
	Name: DefaultText,
	Avatar: DefaultAvatarPictureTransparent,
	ProfileUrl: DefaultUrl,
	ShopUrl: DefaultUrl,
	HouseUrl: DefaultUrl,
	CastleType: 0,
});

export const DefaultShopDesignItem = Object.freeze<MiaPlaza.Controllers.Shop.IDesignedItemData>({
	Author: DefaultAuthorData,
	Price: 0,
	ProductionCost: 0,
	HighestAllowedPrice: 0,
	Thumbnail: DefaultPicture,
	SoldTimes: 0,
	Name: DefaultText,
	Sale: false,
	Item: defaultModelId(),
	ItemShopType: MiaPlaza.Control.Games.Shop.PublicShopType.Furniture,
});

export const DefaultShapeData = Object.freeze<MiaPlaza.Controllers.Shop.IShapeData>({
	Shape: DefaultImageUrl,
});

export const DefaultPurchaseStatistics = Object.freeze<MiaPlaza.LO.Payments.IPurchaseStatistics>({
	PurchaseCount: [],
	FirstSubscriptionDate: DefaultDateString,
	LatestPurchaseDate: DefaultDateString,
});

export const DefaultFriendRequest = Object.freeze<MiaPlaza.Control.Vue.Lists.IFriendRequest>({
	AcceptUrl: DefaultUrl,
	Avatar: DefaultPicture,
	BlockUrl: DefaultUrl,
	DeclineUrl: DefaultUrl,
	MemberUrl: DefaultUrl,
	Name: DefaultText,
});

export const DefaultMember = Object.freeze<MiaPlaza.Control.Vue.Lists.IMemberInfo>({
	AvatarSettings: DefaultJSON,
	Description: DefaultText,
	Name: DefaultText,
	Picture: DefaultPicture,
	ProfileLink: DefaultUrl,
	FriendshipState: MiaPlaza.LO.Social.FriendshipState.CantBeFriends,
});

export const DefaultMemberProfileData = Object.freeze<MiaPlaza.Controllers.Members.IMemberProfileData>({
	Name: DefaultText,
	Picture: DefaultPicture,
	ProfileLink: DefaultUrl,
});

export const DefaultMessage = Object.freeze<MiaPlaza.Control.Vue.Lists.IMessage>({
	DeleteUrl: DefaultUrl,
	DisplayReplyTo: false,
	IsNew: false,
	IsUnread: false,
	MailboxType: MiaPlaza.Content.MessageListType.ReceivedYouOnly,
	MemberAvatar: DefaultPicture,
	MemberName: DefaultText,
	MemberUrl: DefaultUrl,
	MessageReadUrl: DefaultUrl,
	ReplyUrl: DefaultUrl,
	Subject: DefaultText,
	Text: DefaultText,
	Timestamp: DefaultTime,
	IsStarred: false,
	ToggleIsStarredUrl: DefaultUrl,
});

export const DefaultGiveGift = Object.freeze<MiaPlaza.Control.Vue.Lists.IGift>({
	Image: DefaultPicture,
	Link: DefaultUrl,
});

export const DefaultGuestBookEntry = Object.freeze<MiaPlaza.LO.Profile.GuestBook.Dtos.IGuestBookEntry>({
	AuthorAvatar: DefaultPicture,
	AuthorName: DefaultText,
	AuthorUrl: DefaultUrl,
	CreationDate: DefaultTime,
	AllowDelete: false,
	Text: DefaultText,
});

export const DefaultAdminMember = Object.freeze<MiaPlaza.Control.Vue.Lists.IAdminMember>({
	DetailsUrl: DefaultUrl,
	Id: DefaultText,
	Name: DefaultText,
	Type: MiaPlaza.Model.PortalType.Parents,
});

export const DefaultArticle = Object.freeze<MiaPlaza.Control.Vue.Lists.IArticle>({
	Category: DefaultText,
	NewspaperImage: DefaultPicture,
	NewspaperUrl: DefaultUrl,
	PublishingDate: DefaultTime,
	Title: DefaultText,
	TitleUrl: DefaultUrl,
});

export const DefaultBozoMember = Object.freeze<MiaPlaza.LO.Members.BozoMember.Models.IBozoMemberData>({
	Name: DefaultText,
	Id: defaultModelId(),
	AccountDetailsLink: DefaultUrl,
	ReviewerName: DefaultText,
	CreationDate: DefaultText,
});

export const DefaultLandingPageSubjectData = Object.freeze<MiaPlaza.Controllers.Public.ISubjectData>({
	Subject: DefaultSubject,
	Units: [],
	Video: {
		VideoSrc: DefaultUrl,
		ThumbnailSrc: DefaultImageUrl,
	},
});

export const DefaultInventory = Object.freeze<MiaPlaza.Control.Vue.Lists.IInventoryListItem>({
	Type: MiaPlaza.Model.Games.MiniWorld.Type.Designed,
	DeleteLink: DefaultUrl,
	Height: DefaultText,
	Id: DefaultText,
	Image: DefaultPicture,
	Position: false,
	Width: DefaultText,
	PetWorldLink: DefaultUrl,
});

export const DefaultTeam = Object.freeze<MiaPlaza.Control.Vue.Lists.ITeam>({
	IsNew: false,
	NumberOfUsers: 0,
	TeamName: DefaultText,
	TeamUrl: DefaultUrl,
});

export const DefaultTestimonial = Object.freeze<MiaPlaza.LO.Testimonials.ITestimonial>({
	Author: DefaultText,
	Highlight: DefaultText,
	Text: DefaultText,
});

export const DefaultTransaction = Object.freeze<MiaPlaza.Control.Vue.Lists.ITransaction>({
	Picture: DefaultPicture,
	Text: DefaultText,
	Time: DefaultTime,
});

export const DefaultFAQCategory = Object.freeze<MiaPlaza.ConfigModel.FAQ.IFAQCategory>({
	Category: DefaultText,
	AnsweredQuestions: [],
});

export const DefaultAnsweredQuestion = Object.freeze<MiaPlaza.ConfigModel.FAQ.IAnsweredQuestion>({
	Answer: DefaultText,
	Question: DefaultText,
});

export const DefaultAdminTextResource = Object.freeze<MiaPlaza.Control.Vue.Lists.IAdminTextResource>({
	Data: [],
	DeleteUrl: DefaultUrl,
	EditUrl: DefaultUrl,
	Extra: [],
	Key: DefaultText,
	SupportedApplications: [],
	SupportedPortals: [],
});

export const DefaultDesignedItem = Object.freeze<MiaPlaza.Control.Vue.Lists.IDesignedItem>({
	Id: 0,
	Image: DefaultPicture,
	SelectedItemStore: DefaultText,
});

export const DefaultMedalItem = Object.freeze<MiaPlaza.Control.Vue.Lists.IMedalListItem>({
	ImageUrl: DefaultImageUrl,
	GameTitle: DefaultText,
	GameLink: DefaultUrl,
});

export const DefaultDesignTemplate = Object.freeze<MiaPlaza.Controllers.Shop.IDesignTemplateData>({
	Thumbnail: DefaultPicture,
	Price: 0,
	Url: DefaultUrl,
});

export const DefaultNewspaperArticle = Object.freeze<MiaPlaza.Control.Vue.Lists.INewspaperArticle>({
	Body: DefaultText,
	ClickUrl: DefaultUrl,
	CreationDate: DefaultText,
	LinkText: DefaultText,
	Title: DefaultText,
});

export const DefaultCourseReport = Object.freeze<MiaPlaza.LO.Curriculum.Display.ICourseReport>({
	Assessments: [],
	StudyTime: DefaultText,
	Subject: DefaultSubject,
	Course: defaultModelId(),
	Title: DefaultText,
	OverallScore: DefaultText,
	SelectedDatesScore: DefaultText,
});

export const DefaultMiniWorldDraft = Object.freeze<MiaPlaza.Controllers.Shop.IMiniWorldDraft>({
	RedirectLink: DefaultUrl,
	MiniworldDraft: defaultModelId(),
	Image: DefaultText,
	Name: DefaultText,
});

export const DefaultAttendanceReportEntry = Object.freeze<MiaPlaza.LO.Attendance.Dtos.IAttendanceReportEntry>({
	DayAttendanceReports: [],
	Title: DefaultText,
	TotalTime: DefaultTime,
});

export const DefaultNewsletterContent = Object.freeze<MiaPlaza.Control.Vue.Lists.INewsletterContent>({
	Action: MiaPlaza.Control.Vue.Lists.NewsletterAction.None,
	ActionLink: DefaultUrl,
	CreationDate: DefaultTime,
	DeleteLink: DefaultUrl,
	DuplicateLink: DefaultUrl,
	OpenLabel: DefaultText,
	OpenLink: DefaultText,
	Progress: DefaultText,
	Status: DefaultText,
	Subject: DefaultText,
	Newsletter: defaultModelId(),
	ShownOnParentPortal: false,
});

export const DefaultUploadedImage = Object.freeze<MiaPlaza.Control.Vue.Lists.IUploadedImage>({
	CreationDateAndTime: DefaultText,
	DeleteLink: DefaultUrl,
	Id: 0,
	PathOnDisk: DefaultText,
	PathOnWebsite: DefaultText,
});

export const DefaultLostPet = Object.freeze<MiaPlaza.Control.Vue.Lists.ILostPet>({
	GiveBackLink: DefaultUrl,
	OwnerId: 0,
	OwnerName: DefaultText,
	PetName: DefaultText,
	PetPicture: DefaultPicture,
});

export const DefaultAdminSubscriptionEmbedded = Object.freeze<MiaPlaza.Control.Vue.Lists.IAdminSubscriptionEmbedded>({
	Canceled: false,
	Creation: DefaultText,
	ExternalId: DefaultText,
	FutureStartDate: false,
	PastDue: false,
	Product: DefaultText,
	Start: DefaultText,
	SyncUrl: DefaultText,
});

export const DefaultAdminMembershipChangeLogEntry = Object.freeze<MiaPlaza.LO.Memberships.IAdminMembershipChangeLogEntry>({
	Creation: DefaultText,
	MembershipType: DefaultText,
	Reason: DefaultText,
	MembershipValues: DefaultJSON,
});

export const DefaultAdminFriend = Object.freeze<MiaPlaza.Control.Vue.Lists.IAdminFriend>({
	Id: DefaultText,
	Name: DefaultText,
	DetailsUrl: DefaultUrl,
	Started: DefaultText,
});

export const DefaultAdminAccountPayment = Object.freeze<MiaPlaza.Control.Vue.Lists.IAdminAccountPayment>({
	Type: DefaultText,
	Product: DefaultText,
	Creation: DefaultText,
	Amount: DefaultText,
	BraintreeTransactionId: DefaultText,
	Coupon: DefaultText,
});

export const DefaultAnswerAttemptStats = Object.freeze<MiaPlaza.Controllers.Admin.IAnswerAttemptStats>({
	GameId: DefaultText,
	GameName: DefaultText,
	CorrectRate: DefaultText,
	TotalAnswers: DefaultText,
});

export const DefaultAdminCustomGameVersion = Object.freeze<MiaPlaza.Controllers.Admin.ICustomGameVersion>({
	Number: 0,
	Text: DefaultText,
});

export const DefaultAdminCustomGame = Object.freeze<MiaPlaza.Controllers.Admin.ICustomGame>({
	AssessmentLink: DefaultUrl,
	CommonIdentifier: DefaultText,
	LocalVersion: DefaultAdminCustomGameVersion,
	RemoteVersion: DefaultAdminCustomGameVersion,
	IsInstalled: true,
	LessonLink: DefaultUrl,
	PracticeLink: DefaultUrl,
});

export const DefaultAdminProductPrice = Object.freeze<MiaPlaza.Controllers.Admin.IAdminProductPrice>({
	End: DefaultText,
	FirstPeriodPrice: 0,
	Price: 0,
	Product: DefaultText,
	Start: DefaultText,
});

export const DefaultTaskData = Object.freeze<MiaPlaza.Controllers.Curriculum.ILearningPathTaskData>({
	Description: DefaultText,
	Icon: DefaultPicture,
	Link: DefaultUrl,
	Thumbnail: DefaultPicture,
	ThumbnailTitle: DefaultText,
	TaskType: MiaPlaza.LO.Lesson.Dtos.Tasks.TaskType.Assessment,
	ScheduledForToday: true,
	CheckpointAssessment: false,
});

export const DefaultCourseData = Object.freeze<MiaPlaza.Controllers.Curriculum.ICourseData>({
	Title: DefaultText,
});

export const DefaultAssignedCourseData = Object.freeze<MiaPlaza.Controllers.Curriculum.IAssignedCourseData>({
	...DefaultCourseData,
	LearningPathTask: DefaultTaskData,
	State: MiaPlaza.LO.CourseState.None,
	CompletedTasksCount: 0,
	TotalTasksCount: 0,
});

export const DefaultCurriculumCourse = Object.freeze<MiaPlaza.Controllers.Curriculum.Configuration.ICourseData>({
	Title: DefaultText,
	Description: DefaultText,
	RecommendedGradeMin: MiaPlaza.Model.Grades.Grade.Seventh,
	RecommendedGradeMax: MiaPlaza.Model.Grades.Grade.Eighth,
	RecommendedLength: 0,
	RecommendedSchoolWeekLength: 0,
	Enrolled: false,
	EnrollmentStatus: MiaPlaza.LO.Curriculum.Configuration.CourseEnrollmentStatus.InProgress,
	InDevelopment: false,
	Credits: 0,
});

export const DefaultCurriculumUnit = Object.freeze<MiaPlaza.Controllers.Curriculum.Configuration.IUnitData>({
	Title: DefaultText,
	LessonPdfs: [],
});

export const DefaultCurriculumLesson = Object.freeze<MiaPlaza.Controllers.Curriculum.Configuration.ILessonData>({
	Name: DefaultText,
	ExplanationParents: null,
	Progress: null,
	IsTextToSpeechEnabled: false,
	CheckpointAssessmentInLesson: false,
	IsExcused: false,
});

export const DefaultPublicHoliday = Object.freeze<MiaPlaza.Controllers.Curriculum.Configuration.IPublicHolidayData>({
	Date: DefaultTime,
	Observed: false,
	Selected: false,
});

export const DefaultNoServiceDay = Object.freeze<MiaPlaza.Controllers.Curriculum.Configuration.INoServiceDayData>({
	Date: DefaultTime,
	Name: "",
	IconUrl: DefaultImageUrl,
	Selected: false,
});

export const DefaultCustomBreak = Object.freeze<MiaPlaza.Controllers.Curriculum.Configuration.ICustomBreakData>({
	Name: DefaultText,
	Start: DefaultTime,
	End: DefaultTime,
});

export const DefaultScheduleDay = Object.freeze<MiaPlaza.LO.Curriculum.Scheduling.Calendar.Dtos.IDayData>({
	CustomBreak: undefined,
	PublicHoliday: undefined,
	NoServiceDay: undefined,
	CompletedTasksCount: 0,
});

export const DefaultScheduleUnit = Object.freeze<MiaPlaza.LO.Curriculum.Scheduling.Calendar.Dtos.IUnitData>({
	UnitId: defaultModelId(),
	Subject: DefaultSubject,
	Title: DefaultText,
	CourseTitle: DefaultText,
});

export const DefaultScheduleTask = Object.freeze<MiaPlaza.LO.Curriculum.Display.ITaskAssignmentData>({
	TaskAssignmentId: defaultModelId(),
	Image: DefaultPicture,
	Name: DefaultText,
	Status: MiaPlaza.Controllers.Curriculum.Enums.ProgressStatus.None,
	Type: MiaPlaza.LO.Lesson.Dtos.Tasks.TaskType.Assessment,
	PreviewData: {
		Title: DefaultText,
	},
	Icon: DefaultPicture,
	CheckpointAssessment: false,
	PdfSplitData: null,
});

export const DefaultSearchScheduleTask = Object.freeze<MiaPlaza.Controllers.Curriculum.Schedule.ISearchTaskAssignmentData>({
	...DefaultScheduleTask,
	ScheduledDate: DefaultDateString,
	Unit: DefaultText,
	Subject: DefaultSubject,
});

export const DefaultPdfSplitData = Object.freeze<MiaPlaza.LO.Curriculum.Display.IPdfSplitData>({
	SectionNumber: 1,
	ParentPdfTitle: DefaultText,
	ParentPdfUrl: DefaultUrl,
});

export const DefaultArcadeGame = Object.freeze<MiaPlaza.Control.Vue.Lists.IArcadeGame>({
	Name: DefaultText,
	Thumbnail: DefaultPicture,
	Url: DefaultUrl,
});

export const DefaultMiniMeClosetItem = Object.freeze<MiaPlaza.LO.MiniMe.MiniMeClosetService.IMiniMeClosetItem>({
	Id: DefaultText,
	ImageSrc: DefaultImageUrl,
	ItemType: MiaPlaza.LO.MiniMe.MiniMeClosetService.MiniMeClosetItemType.Default,
	Category: MiaPlaza.LO.MiniMe.MiniMeClosetService.MiniMeClosetItemCategory.Face,
	ObjectChains: [],
	X: 0,
	Y: 0,
	Width: 0,
	Height: 0,
});

export const DefaultTopEarner = Object.freeze<MiaPlaza.Controllers.Admin.ITopEarner>({
	ChildName: DefaultText,
	ChildProfileLink: DefaultText,
	VirtualMoney: 0,
	EarningsOverPeriod: 0,
	Child: defaultModelId(),
	CheaterStatus: MiaPlaza.Model.CheaterStatus.None,
});

export const DefaultCompetitionWinner = Object.freeze<MiaPlaza.Controllers.Admin.ICompetitionWinner>({
	Name: DefaultText,
	ProfileLink: DefaultText,
	CompetitionDate: DefaultDateString,
	CompetitionType: MiaPlaza.Model.CompetitionType.MostVirtualCurrencyEarned,
	MoneyEarned: 0,
	PrizeName: DefaultText,
});

export const DefaultCompetitionLeader = Object.freeze<MiaPlaza.Controllers.Admin.ICompetitionLeader>({
	Name: DefaultText,
	ProfileLink: DefaultText,
	MoneyEarned: 0,
});

export const DefaultQuizAssessmentAttempt = Object.freeze<MiaPlaza.Controllers.Gradebook.IQuizAssessmentAttempt>({
	Finished: DefaultText,
	Score: 0,
	Passed: true,
});

export const DefaultActiveModerator = Object.freeze<MiaPlaza.Controllers.Admin.IActiveModerator>({
	Name: DefaultText,
	Moderator: defaultModelId(),
	NumberOfLocks: 0,
});

export const DefaultOnlineSchoolChildData = Object.freeze<MiaPlaza.LO.OnlineSchoolAndPlus.Models.IChildData>({
	Name: DefaultText,
	Id: null,
	UUId: DefaultText,
});

export const DefaultAssessmentAttemptNotificationSettings = Object.freeze<MiaPlaza.LO.Assessment.IAssessmentAttemptNotificationSettings>({
	MaxAssessmentAttemptAmount: 0,
	Enabled: false,
});

export const DefaultOnlineSchoolStudentInformation = Object.freeze<MiaPlaza.LO.OnlineSchool.Models.IOnlineSchoolStudentInformation>({
	Child: DefaultOnlineSchoolChildData,
	SapLink: null,
	EMail: null,
	CounselorUuid: null,
	AdvisorUuid: null,
	IsPawsPartnerStudent: false,
});

export const DefaultPlusStudentInformation = Object.freeze<MiaPlaza.LO.Plus.Models.IPlusStudentInformation>({
	Child: DefaultOnlineSchoolChildData,
	EMail: null,
});

export const DefaultOnlineSchoolParentData = Object.freeze<MiaPlaza.LO.OnlineSchoolAndPlus.Models.IParentData>({
	Name: DefaultText,
	Url: DefaultUrl,
});

export const DefaultOnlineSchoolStudent = Object.freeze<MiaPlaza.Controllers.Admin.OnlineSchool.IOnlineSchoolStudent>({
	ChildData: DefaultOnlineSchoolChildData,
	ParentData: DefaultOnlineSchoolParentData,
	NumberOfUngradedOrMissingCheckpoints: 0,
	DateOfLastCompletedTask: DefaultDateString,
	SapLink: DefaultText,
	IsActive: false,
	EMail: DefaultText,
	UUId: DefaultText,
});

export const DefaultTloReportStudentLearningOpportunity: MiaPlaza.PublicOnlineSchool.Application.Reports.TotalLearningOpportunity.IStudentLearningOpportunity = Object.freeze({
	StudentName: DefaultText,
	Email: DefaultText,
	ParentEmail: DefaultText,
	YearlyHoursStatus: MiaPlaza.PublicOnlineSchool.Domain.YearlyHoursStatus.OnTrack,
	InactiveHours: 0,
	Advisor: DefaultText,
	SchoolDays: 0,
	PercentVsExpected: 0,
	PercentThroughSchoolYear: 0,
	DateOfLastCompletedTask: DefaultText,
	PendingOfflineActivityHours: 0,
	Ssid: DefaultText,
	EnrollmentStartDate: DefaultText,
	EnrollmentEndDate: DefaultText,
	TotalEnroll: 0,
	ComputerBasedHours: 0,
	OtherHours: 0,
	TotalDocumentedHours: 0,
	PercentOfTime: 0,
	DifferenceInHoursVsExpected: 0,
	OnlineSchoolStudentUuid: defaultModelId(),
});

export const DefaultRejectedActivity: MiaPlaza.LO.Attendance.Dtos.IRejectedActivity = Object.freeze({
	ActivityName: DefaultText,
	ActivityType: null,
	UpdatedAt: DefaultDateString,
	Course: {
		Course: defaultModelId<"Curriculum.Course">(),
		Subject: MiaPlaza.Model.Subject.Math,
		Title: DefaultText,
	},
	StudentName: DefaultText,
	DurationInMinutes: 0,
	Date: DefaultDateString,
	ParentName: DefaultText,
	SubmittedBy: MiaPlaza.LO.Attendance.Dtos.ActivitySubmitterRole.Unknown,
	ReviewedBy: DefaultText,
	RejectionReason: DefaultText,
});

export const DefaultPlusStudent = Object.freeze<MiaPlaza.Controllers.Admin.Plus.IPlusStudent>({
	UUId: DefaultText,
	ChildData: DefaultOnlineSchoolChildData,
	ParentData: DefaultOnlineSchoolParentData,
	NumberOfUngradedOrMissingCheckpoints: 0,
	DateOfLastCompletedTask: DefaultDateString,
	EMail: DefaultText,
});

export const DefaultCheckpointReport = Object.freeze<MiaPlaza.Controllers.Admin.CheckpointReports.ICheckpointReport>({
	Uuid: DefaultText,
	ReporterName: DefaultText,
	ReporterId: 0,
	CreatedAt: DefaultText,
	SelectedReasonKey: DefaultText,
	AdditionalProvidedInfo: DefaultText,
	ReporterUuid: DefaultText,
	OnlineSchoolBaseUrl: DefaultText,
});

export const DefaultFailedSubscriptionPause = Object.freeze<MiaPlaza.Controllers.Admin.FailedSubscriptionPauses.IFailedSubscriptionPause>({
	SubscriptionPause: defaultModelId(),
	StudentId: 0,
	StudentName: DefaultText,
	StudentAccountDetailsLink: DefaultUrl,
	StudentManageMembershipLink: DefaultUrl,
	ParentId: 0,
	ParentName: DefaultText,
	ParentEmail: DefaultText,
	ParentAccountDetailsLink: DefaultUrl,
	BraintreeSubscriptionId: DefaultText,
	BraintreeTransactionId: DefaultText,
	StartDate: DefaultDateString,
	EndDate: DefaultDateString,
	RefundAmount: DefaultText,
});

export const DefaultGiveFreeMonthImportRowInfo = Object.freeze<MiaPlaza.Controllers.Admin.IGiveFreeMonthImportRowInfo>({
	ParentEmail: DefaultText,
	StudentFirstName: DefaultText,
	ParentName: DefaultText,
	ParentAccountLink: DefaultUrl,
	StudentName: DefaultText,
	StudentAccountLink: DefaultUrl,
	Student: null,
	MembershipType: DefaultText,
	PaidThrough: DefaultDateString,
	CanGiveFreeMonth: false,
	AlreadyGivenFreeMonth: false,
	RejectReason: DefaultText,
});

export const DefaultLessonFeedback = Object.freeze<MiaPlaza.Controllers.Admin.LessonFeedback.ILessonFeedback>({
	StudentId: 0,
	SubmitDate: DefaultLocalDateTime,
	Course: DefaultText,
	Unit: DefaultText,
	Lesson: DefaultText,
	TaskType: MiaPlaza.LO.Lesson.Dtos.Tasks.TaskType.Practice,
	MinLevel: 0,
	MaxLevel: 0,
	Difficulty: 0,
	Enjoyment: 0,
	AdditionalFeedback: DefaultText,
});

export const DefaultVirtualCurrencyTransaction = Object.freeze<MiaPlaza.Controllers.Admin.IVirtualCurrencyTransaction>({
	Reason: DefaultText,
	Type: DefaultText,
	PreviousAmount: 0,
	Delta: 0,
	DateTime: DefaultLocalDateTime,
});

export const DefaultAdminCouponCode = Object.freeze<MiaPlaza.Control.Vue.Lists.IAdminCouponCode>({
	Code: DefaultText,
	CouponCodeDiscounts: [],
	Expiration: DefaultDateString,
	IsUsed: true,
	UUId: defaultModelId(),
	Notes: DefaultText,
	SelectedItemStore: DefaultText,
	VisitorCount: 0,
});

export const DefaultUploaderData = Object.freeze<MiaPlaza.Controllers.Video.IUploaderData>({
	Author: DefaultText,
	AuthorAvatar: DefaultPicture,
	MemberUrl: DefaultUrl,
});

export const DefaultVideoData = Object.freeze<MiaPlaza.Controllers.Video.IVideoData>({
	Uploader: DefaultUploaderData,
	AvailableSince: DefaultInstantString,
	Thumbnail: DefaultPicture,
	VideoUrl: DefaultUrl,
	Title: DefaultText,
	Duration: DefaultDuration,
	IsNew: false,
});

export const DefaultNamedLink = Object.freeze<MiaPlaza.ConfigModel.ResourceLinks.INamedLink>({
	Url: DefaultUrl,
	Title: DefaultText,
});

export const DefaultFilmmaker = Object.freeze<MiaPlaza.Controllers.Video.IFilmmakerData>({
	Name: DefaultText,
	Avatar: DefaultPicture,
	ProfileUrl: DefaultUrl,
});

export const DefaultBuyerData = Object.freeze<MiaPlaza.Controllers.Admin.IBuyerData>({
	Id: 0,
	AccountLink: DefaultUrl,
	Firstname: DefaultText,
	Lastname: DefaultText,
	EMail: DefaultText,
	NumberOfChildren: 0,
	ChildrenLogins: 0,
	PurchaseStatistics: DefaultPurchaseStatistics,
});

export const DefaultUnitData = Object.freeze<MiaPlaza.LO.Curriculum.AllLessons.Dtos.IUnitData>({
	Title: DefaultText,
	CourseTitle: DefaultText,
	IsExcused: false,
	Lessons: [],
});

export const DefaultSubjectData = Object.freeze<MiaPlaza.LO.Curriculum.AllLessons.Dtos.ISubjectData>({
	Subject: DefaultSubject,
});

export const DefaultLessonData = Object.freeze<MiaPlaza.LO.Curriculum.AllLessons.Dtos.ILessonData>({
	Name: DefaultText,
	ThumbnailUrl: DefaultImageUrl,
	Link: DefaultUrl,
	HighestMedal: MiaPlaza.Model.Games.MedalType.None,
	Subject: DefaultSubject,
	CheckpointAssessmentInLesson: false,
	IsExcused: false,
	IsLessonAvailableInAllLessonsView: true,
});

export const DefaultShoutboxLine = Object.freeze<MiaPlaza.Controllers.ShoutBox.IShoutboxLineData>({
	ShoutboxLine: defaultModelId(),
	Author: DefaultText,
	Text: DefaultText,
	ProfileImage: DefaultPicture,
	ProfileLink: DefaultUrl,
	CreationDate: DefaultDateString,
	CanPublish: false,
	CanDelete: false,
	CanSetVisibility: false,
	IsPublic: false,
});

export const DefaultTeamMember = Object.freeze<MiaPlaza.Control.Vue.ITeamMember>({
	Title: DefaultText,
	About: DefaultText,
	PictureUrl: DefaultText,
});

export const DefaultCourseGrade = Object.freeze<MiaPlaza.LO.Curriculum.ICourseGrade>({
	AssessmentAverage: 0,
	CheckpointAverage: 0,
	OverallScoreText: DefaultText,
});

export const DefaultCourseProgress = Object.freeze<MiaPlaza.LO.Curriculum.ICourseProgress>({
	Actual: 0,
	Expected: 0,
	Behind: false,
});

export const DefaultGradebookCourse = Object.freeze<MiaPlaza.Controllers.Gradebook.IGradebookCourse>({
	Course: defaultModelId(),
	Subject: DefaultSubject,
	Title: DefaultText,
	NonZeroGradedCheckpoints: 0,
	TotalCheckpoints: 0,
	InDevelopment: false,
	Grade: DefaultCourseGrade,
	StartDate: DefaultDateString,
	EndDate: DefaultDateString,
	Progress: DefaultCourseProgress,
	CompletedUngradedCheckpoints: 0,
	Credits: 0,
	PassingScorePercentage: null,
});

export const DefaultGradebookAssignment = Object.freeze<MiaPlaza.Controllers.Gradebook.IGradebookAssignment>({
	Title: DefaultText,
	BestAttemptUUId: DefaultText,
	IsConsideredPassed: false,
	IsAssessment: true,
	IsExcused: false,
	IsMissing: false,
	HasPassingGrade: false,
	TaskCompleted: false,
	TaskViewed: false,
	ViewedOn: DefaultDateString,
	Completion: DefaultDateString,
	Grade: null,
	Attempts: 0,
	AttemptLimit: null,
	Course: defaultModelId(),
	Lesson: defaultModelId(),
	LessonTitle: DefaultText,
});

export const DefaultConfigureCurriculumSubject = Object.freeze<MiaPlaza.LO.Curriculum.Configuration.IEnrolledCoursesInSubject>({
	Subject: DefaultSubject,
	Courses: [],
});

export const DefaultCourseEnrollment = Object.freeze<MiaPlaza.LO.Curriculum.Configuration.ICourseEnrollmentData>({
	Title: DefaultText,
	Course: defaultModelId(),
	UUId: defaultModelId(),
	Status: MiaPlaza.LO.Curriculum.Configuration.CourseEnrollmentStatus.InProgress,
	Start: DefaultText,
	End: DefaultText,
	SchoolWeek: [],
	InDevelopment: false,
	PdfUrl: DefaultText,
	WorkbookUrl: DefaultText,
	ShowWorkbook: true,
});

export const DefaultReportTask = Object.freeze<MiaPlaza.LO.Curriculum.Display.IAssessmentData>({
	Name: DefaultText,
	Progress: DefaultText,
	Instant: DefaultDateString,
	Date: DefaultLocalDateTime,
	DetailsLink: DefaultUrl,
	IncludeInPrintableReport: false,
	UUId: DefaultText,
	CheckpointAssessment: false,
	IsExcused: false,
	IsMissing: false,
});

export const DefaultHouse = Object.freeze<MiaPlaza.Controllers.MiniWorld.IHouse>({
	ChildName: DefaultText,
	ChildProfileLink: DefaultUrl,
	MiniWorldHouseLink: DefaultUrl,
	ChildAvatar: DefaultPicture,
	Castle: DefaultPicture,
});

export const DefaultTeamAchievements = Object.freeze<MiaPlaza.Controllers.TeamAchievements.ITeamAchievements>({
	TeamName: DefaultText,
	TeamProfileLink: DefaultText,
	GoldMedals: 0,
	SilverMedals: 0,
	BronzeMedals: 0,
});

export const DefaultCourseEnrollmentShift = Object.freeze<MiaPlaza.LO.Curriculum.Configuration.ICourseEnrollmentShift>({
	Title: DefaultText,
	CurrentInterval: [DefaultLocalDateTime, DefaultLocalDateTime],
	AdjustedInterval: [DefaultLocalDateTime, DefaultLocalDateTime],
	CourseEnrollment: defaultModelId(),
});

export const DefaultRankedListItem = Object.freeze<MiaPlaza.Controllers.RankedList.IListItem>({
	MemberName: DefaultText,
	MemberUrl: DefaultUrl,
	IsCurrentMember: false,
	Value: DefaultText,
});

export const DefaultSpecialUser = Object.freeze<MiaPlaza.Controllers.Admin.ISpecialUserData>({
	ProfileLink: DefaultUrl,
	FirstAndLastName: DefaultText,
	SpecialUserType: MiaPlaza.Model.SpecialUserType.DEFAULT_MEMBER,
	EMail: DefaultText,
});

export const DefaultLessonTask = Object.freeze<MiaPlaza.LO.Lesson.Dtos.Tasks.ITask>({
	Id: defaultModelId(),
	Type: MiaPlaza.LO.Lesson.Dtos.Tasks.TaskType.Practice,
	TaskTypeIsCheckpoint: false,
	Assigned: true,
	Completed: false,
	ShouldAskForFeedback: false,
});

export const DefaultUserUploadedVideo = Object.freeze<MiaPlaza.Controllers.Moderation.IUserUploadedVideo>({
	Name: DefaultText,
	HintsForModerator: DefaultText,
	UploadDate: DefaultDateString,
	UploaderName: DefaultText,
	UploaderProfileUrl: DefaultUrl,
	VideoUrl: DefaultUrl,
	ModerationApproval: MiaPlaza.Model.Video.ApprovalLevel.NotDecided,
	ParentApproval: MiaPlaza.Model.Video.ApprovalLevel.NotDecided,
	Thumbnail: DefaultPicture,
	ApprovedByChild: true,
	ModerationStatusTextResourceKey: DefaultText,
	ModerationApprovalComment: DefaultText,
	ModerationMessageForParents: DefaultText,
	ModerationRejectionReason: DefaultText,
	ModeratorRating: MiaPlaza.Model.Video.ModerationRating.Good,
	IsEducational: false,
});

export const DefaultChildPenaltyItem = Object.freeze<MiaPlaza.Controllers.Admin.Moderation.IChildPenaltyItem>({
	PenaltyType: DefaultText,
	IsActive: true,
	CreationDate: DefaultDateString,
	Until: DefaultDateString,
	Reason: {
		PenaltyType: MiaPlaza.LO.Moderation.Enums.ChildPenaltyType.Simple,
	},
	ChildPenalty: defaultModelId(),
});

export const DefaultShopCompetitionItem = Object.freeze<MiaPlaza.Controllers.Shop.IShopWithCustomers>({
	Name: DefaultText,
	Avatar: DefaultPicture,
	ProfileUrl: DefaultUrl,
	ShopUrl: DefaultUrl,
	HouseUrl: DefaultUrl,
	NumberOfCustomers: 0,
});

export const DefaultStreakWorld = Object.freeze<MiaPlaza.LO.Streaks.Dtos.IStreakWorld>({
	Id: "mountain",
	Name: "Mountain",
	LastMilestone: 0,
	BackgroundUrl: DefaultImageUrl,
	IconUrl: DefaultImageUrl,
	Theme: "light",
});

export const DefaultStreakLeaderboardMember = Object.freeze<MiaPlaza.LO.Streaks.Dtos.IStreakLeaderboardMember>({
	Name: DefaultText,
	MemberUrl: DefaultUrl,
	IsCurrentMember: false,
	StreakDay: 0,
	Avatar: DefaultPicture,
	Rank: 0,
	World: DefaultStreakWorld,
});

export const DefaultNumberOfAssessmentsToBeLockedInCourse = Object.freeze<MiaPlaza.LO.Curriculum.Configuration.INumberOfAssessmentsToBeLockedInCourse>({
	Course: defaultModelId(),
	CourseTitle: DefaultText,
	CurrentAttemptLimit: 3,
	NumberOfAssessmentsToBeLocked: 0,
});
